'use client';
import { FieldFooter } from '@lichtblick/photon/atoms/field-footer';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { ExclamationMarkTriangleXs } from '@lichtblick/photon/icons/symbols/exclamation-mark-triangle-xs';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { SubmitButton } from '@/components/form-box/submit-button';
import { CheckoutFlowFormBox } from '@/components/steps/checkout-flow-form-box';
import type { StepProps } from '@/lib/flow/types';
import { CheckboxCard } from './checkbox-card';
import {
  IS_INTERESTED_IN_HEAT_PUMP,
  IS_INTERESTED_IN_PV,
  IS_INTERESTED_IN_STROM_WALLET,
  IS_INTERESTED_IN_WALLBOX,
} from './product-interest.const';
import styles from './product-interest-form.module.css';
import type { ProductInterestFormState } from './product-interest-step';

type ProductInterestFormProps = Pick<StepProps<ProductInterestFormState>, 'action' | 'state' | 'stepId'> & {
  children?: ReactNode;
};

type ProductInterestState = ProductInterestFormState & { hasError: boolean };

const togglePv = (prev: ProductInterestState): ProductInterestState => {
  if (prev.interestInPv === 'off') {
    return { ...prev, interestInPv: 'on', hasError: false };
  }

  const stateUpdate: Partial<ProductInterestState> = {
    interestInPv: 'off',
    interestInStromWallet: 'off',
  };

  if (prev.interestInHeatPump === 'off') {
    stateUpdate.hasError = true;
    stateUpdate.interestInWallBox = 'off';
  }

  return { ...prev, ...stateUpdate };
};

const toggleHeatPump = (prev: ProductInterestState): ProductInterestState => {
  if (prev.interestInHeatPump === 'off') {
    return { ...prev, interestInHeatPump: 'on', hasError: false };
  }
  const stateUpdate: Partial<ProductInterestState> = { interestInHeatPump: 'off' };
  if (prev.interestInPv === 'off') {
    stateUpdate.hasError = true;
    stateUpdate.interestInWallBox = 'off';
  }
  return { ...prev, ...stateUpdate };
};

export const ProductInterestForm = ({ action, stepId, state }: ProductInterestFormProps) => {
  const [localState, setLocalState] = useState<ProductInterestState>({
    interestInPv: state.interestInPv ?? 'off',
    interestInHeatPump: state.interestInHeatPump ?? 'off',
    interestInStromWallet: state.interestInStromWallet ?? 'off',
    interestInWallBox: state.interestInWallBox ?? 'off',
    hasError: false,
  });

  return (
    <CheckoutFlowFormBox action={action} className={styles.form}>
      <input type="hidden" name="stepId" value={stepId} />
      <CheckboxCard forcedError={localState.hasError}>
        <CheckboxCard.Option
          label="Solaranlage"
          dataTestId={`checkbox-${IS_INTERESTED_IN_PV}`}
          footer="mit / ohne Batteriespeicher"
        >
          <CheckboxCard.Checkbox
            checked={localState.interestInPv === 'on'}
            name={IS_INTERESTED_IN_PV}
            onInvalid={(e) => {
              e.preventDefault();
              setLocalState((prev) => ({ ...prev, hasError: true }));
            }}
            onChange={() => setLocalState(togglePv)}
            required={localState.interestInHeatPump === 'off'}
            forcedError={localState.hasError}
          />
        </CheckboxCard.Option>
        <CheckboxCard.Option
          footer="Dynamischer Tarif mit intelligenter Steuerung"
          label="StromWallet"
          dataTestId={`checkbox-${IS_INTERESTED_IN_STROM_WALLET}`}
        >
          <CheckboxCard.Checkbox
            checked={localState.interestInStromWallet === 'on'}
            onChange={() =>
              setLocalState((prev) => ({
                ...prev,
                interestInStromWallet: prev.interestInStromWallet === 'on' ? 'off' : 'on',
              }))
            }
            name={IS_INTERESTED_IN_STROM_WALLET}
            disabled={localState.interestInPv === 'off'}
          />
        </CheckboxCard.Option>
      </CheckboxCard>

      <CheckboxCard data-testid={`checkbox-${IS_INTERESTED_IN_HEAT_PUMP}`} forcedError={localState.hasError}>
        <CheckboxCard.Option footer="Mit Strom günstiger heizen" label="Wärmepumpe">
          <CheckboxCard.Checkbox
            checked={localState.interestInHeatPump === 'on'}
            onChange={() => setLocalState(toggleHeatPump)}
            onInvalid={(e) => {
              e.preventDefault();
              setLocalState((prev) => ({ ...prev, hasError: true }));
            }}
            name={IS_INTERESTED_IN_HEAT_PUMP}
            required={localState.interestInPv === 'off'}
            forcedError={localState.hasError}
          />
        </CheckboxCard.Option>
      </CheckboxCard>
      {localState.hasError && (
        <FieldFooter
          className={styles['error-message']}
          data-testid="field-error"
          error
          icon={<ExclamationMarkTriangleXs />}
        >
          Hier fehlt noch eine Angabe
        </FieldFooter>
      )}

      <div>
        <Text className={styles['add-on-text']} size="s" renderAs="div" bold>
          Add-on
        </Text>

        <CheckboxCard data-testid={`checkbox-${IS_INTERESTED_IN_WALLBOX}`}>
          <CheckboxCard.Option footer="Elektrofahrzeug zuhause laden" label="Wallbox">
            <CheckboxCard.Checkbox
              checked={localState.interestInWallBox === 'on'}
              disabled={localState.interestInPv === 'off' && localState.interestInHeatPump === 'off'}
              onChange={() =>
                setLocalState((prev) => ({
                  ...prev,
                  interestInWallBox: prev.interestInWallBox === 'on' ? 'off' : 'on',
                }))
              }
              name={IS_INTERESTED_IN_WALLBOX}
            />
          </CheckboxCard.Option>
        </CheckboxCard>
      </div>

      <SubmitButton />
    </CheckoutFlowFormBox>
  );
};
